import * as React from 'react';
import { StyleSheet } from 'react-native';
import { View, Text, Input, CheckBox, Dropdown } from '../../../../components/Themed';
import Icon from '../../../../components/Icon';
import MultipleChoicesOptions from '../ControlTypeOptions/MultipleChoiceOptions';
import TextBoxOptions from '../ControlTypeOptions/TextBoxOptions';
import { match } from 'ts-pattern';
import Colors from '../../../../constants/Colors';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { ControlType } from '../../types';

type Props = {
  index: number;
  question: any;
};

export default function FormQuestion(props: Props) {
  const { index, question } = props;

  const {
    control,
    formState: { errors },
    setValue,
    watch,
    register,
  } = useFormContext();

  const controlType = [
    { value: 1, text: 'Radio' },
    { value: 2, text: 'Checkbox' },
    { value: 3, text: 'Text' },
    { value: 4, text: 'Phone' },
    { value: 5, text: 'Email' },
    { value: 6, text: 'Date' },
    { value: 7, text: 'Number' },
    { value: 8, text: 'Box' },
  ];

  const { fields, remove, move } = useFieldArray({
    control,
    name: 'questions',
  });

  const QuestionContainer = (props: any) => (
    <View key={question.id} style={[{ zIndex: 100 - index }, styles.questionContainer]}>
      {props.children}
    </View>
  );

  const QuestionContainerHeader = (props: any) => <View style={styles.questionContainerHeader}>{props.children}</View>;

  const QuestionIndexText = () => <Text>{index + 1}.</Text>;

  const QuestionButtonContainer = (props: any) => <View style={styles.questionButtonContainer}>{props.children}</View>;

  const MoveQuestionUpButton = () => (
    <Icon name="chevron-up" size={24} color={Colors.primary_500} onPress={() => move(index, index - 1)} />
  );

  const MoveQuestionDownButton = () => (
    <Icon name="chevron-down" size={24} color={Colors.primary_500} onPress={() => move(index, index + 1)} />
  );

  const DeleteQuestionButton = () => (
    <Icon name="trash" size={24} color={Colors.primary_500} onPress={() => remove(index)} />
  );

  const QuestionTextInputBox = () => (
    <Input
      {...register(`questions.${index}.questionText`, { required: true })}
      placeholder="Text"
      label="Text"
      lightColor={Colors.tinted_grey_100}
      darkColor={Colors.nuetral_grey_700}
      control={control}
      errors={errors}
      name={`questions.${index}.questionText`}
      required={true}
      errorMessage="Question Text is required"
    />
  );

  const QuestionCheckboxContainer = (props: any) => (
    <View style={styles.questionCheckboxContainer}>{props.children}</View>
  );

  const RequiredCheckbox = () => (
    <CheckBox
      label={'Required'}
      control={control}
      errors={errors}
      name={`questions.${index}.required`}
      required={false}
    ></CheckBox>
  );

  const HiddenCheckbox = () => (
    <CheckBox
      label={'Hidden'}
      control={control}
      errors={errors}
      name={`questions.${index}.hidden`}
      required={false}
    ></CheckBox>
  );

  const ControlTypeDropdown = () => (
    <Dropdown
      data={controlType}
      style={styles.controlTypeDropdown}
      placeholder="Type"
      lightColor={Colors.tinted_grey_100}
      darkColor={Colors.nuetral_grey_700}
      name={`questions.${index}.type`}
      control={control}
      errors={errors}
      required={false}
      label="Control Type"
      setValue={setValue}
    ></Dropdown>
  );

  const ControlTypeOptions = () => (
    <>
      {match(watch(`questions.${index}.type`)?.text)
        .with(ControlType.Radio, ControlType.Checkbox, ControlType.Box, () => (
          <MultipleChoicesOptions questionIndex={index} control={control} errors={errors} />
        ))
        .with(ControlType.Text, ControlType.Date, ControlType.Email, ControlType.Number, ControlType.Phone, () => (
          <TextBoxOptions questionIndex={index} control={control} errors={errors} />
        ))
        .otherwise(() => (
          <></>
        ))}
    </>
  );

  return (
    <QuestionContainer>
      <QuestionContainerHeader>
        <QuestionIndexText />
        <QuestionButtonContainer>
          {index > 0 && <MoveQuestionUpButton />}
          {index < fields.length - 1 && <MoveQuestionDownButton />}
          <DeleteQuestionButton />
        </QuestionButtonContainer>
      </QuestionContainerHeader>
      <QuestionTextInputBox />
      <QuestionCheckboxContainer>
        <RequiredCheckbox />
        <HiddenCheckbox />
      </QuestionCheckboxContainer>
      <ControlTypeDropdown />
      <ControlTypeOptions />
    </QuestionContainer>
  );
}

const styles = StyleSheet.create({
  questionContainerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  questionContainer: {
    gap: 16,
    borderColor: Colors.nuetral_grey_400,
    borderWidth: 1,
    padding: 8,
    borderRadius: 8,
  },
  questionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  questionButtonContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  questionCheckboxContainer: {
    flexDirection: 'row',
    gap: 16,
  },
  controlTypeDropdown: {
    zIndex: 1000,
    elevation: 1000,
  },
});
